import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
} from "react-hook-form";

import {
  ValidationOptions,
  validationOptionsToControlRules,
} from "../../types/Validations";

export type APSliderRangeFilter =
  | { type: "any" }
  | { type: "lte"; value: number }
  | { type: "gte"; value: number }
  | { type: "eq"; value: number }
  | { type: "between"; min: number; max: number };

type APSliderConfig = {
  type: "range" | "single";
  includeAll?: "start" | "end" | "both";
  openUpperBound?: boolean;
  openLowerBound?: boolean;
  closedUpperBound?: boolean;
  alwaysGreaterThan?: boolean;
  alwaysLessThan?: boolean;
};

interface APSliderProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  validations: ValidationOptions;
  defaultValue: PathValue<T, Path<T>> | undefined;
  minValue: number;
  maxValue: number;
  step: number;
  readOnly?: boolean;
  valueFormatter?: (value: number) => string;
  config: APSliderConfig;
  disabled?: boolean;
}

const APSlider = <T extends FieldValues>({
  name,
  label,
  control,
  validations,
  defaultValue,
  minValue,
  maxValue,
  step,
  valueFormatter,
  config,
  disabled,
}: APSliderProps<T>) => {
  const adjustedStart =
    config.includeAll === "start" ? minValue - step : minValue;
  const adjustedEnd = config.includeAll === "end" ? maxValue + step : maxValue;

  const rangeFilterToSliderValue = (
    selection: APSliderRangeFilter
  ): [number] | [number, number] => {
    switch (selection.type) {
      case "any":
        if (config.type === "range") {
          return [minValue, maxValue];
        } else {
          if (config.includeAll === "end") {
            return [adjustedEnd];
          } else {
            return [adjustedStart];
          }
        }
      case "lte":
        if (config.type === "range") {
          return [minValue, selection.value];
        } else {
          return [selection.value];
        }
      case "gte":
        if (config.type === "range") {
          return [selection.value, maxValue];
        } else {
          return [selection.value];
        }
      case "eq":
        return [selection.value];
      case "between":
        return [selection.min, selection.max];
    }
  };

  const sliderValueToRangeFilter = (
    value: [number] | [number, number]
  ): APSliderRangeFilter => {
    // Handle the "Any" cases
    if (config.includeAll === "start" && value[0] === adjustedStart) {
      return { type: "any" };
    } else if (config.includeAll === "end" && value[0] === adjustedEnd) {
      return { type: "any" };
    }

    if (value.length === 1) {
      // Handle the single value cases
      let type: "gte" | "lte" | "eq";

      if (
        config.alwaysGreaterThan ||
        (config.openUpperBound && value[0] === maxValue)
      ) {
        type = "gte";
      } else if (
        config.alwaysLessThan ||
        (config.openLowerBound && value[0] === minValue)
      ) {
        type = "lte";
      } else {
        type = "eq";
      }

      return { type, value: value[0] };
    }

    // Handle the range cases
    if (
      config.includeAll === "both" &&
      value[0] === adjustedStart &&
      value[1] === adjustedEnd
    ) {
      return { type: "any" };
    } else if (config.openUpperBound && value[1] === maxValue) {
      return { type: "gte", value: value[0] };
    } else if (config.openLowerBound && value[0] === minValue) {
      return { type: "lte", value: value[1] };
    } else {
      return { type: "between", min: value[0], max: value[1] };
    }
  };

  const labelValueFormatter = (rangeFilter: APSliderRangeFilter) => {
    let startText: string;
    let endText: string | null = null;

    switch (rangeFilter.type) {
      case "any":
        startText = "Any";
        break;
      case "lte":
        startText = `${
          valueFormatter ? valueFormatter(rangeFilter.value) : rangeFilter.value
        }${rangeFilter.value !== 0 ? " or less" : ""}`;
        break;
      case "gte":
        startText = `${
          valueFormatter ? valueFormatter(rangeFilter.value) : rangeFilter.value
        }${
          config.closedUpperBound && rangeFilter.value === maxValue
            ? ""
            : " or more"
        }`;
        break;
      case "eq":
        startText = `${
          valueFormatter ? valueFormatter(rangeFilter.value) : rangeFilter.value
        }`;
        break;
      case "between": {
        startText = `${
          valueFormatter ? valueFormatter(rangeFilter.min) : rangeFilter.min
        }`;
        const startEndAreEqual = rangeFilter.min === rangeFilter.max;

        endText = startEndAreEqual
          ? null
          : `${
              valueFormatter ? valueFormatter(rangeFilter.max) : rangeFilter.max
            }`;

        break;
      }
    }

    return (
      <>
        <Typography variant="titleMedium">
          {rangeFilter.type !== "any" ? <b>{startText}</b> : startText}
        </Typography>
        {endText && (
          <Typography variant="titleMedium">
            {" "}
            to <b>{endText}</b>
          </Typography>
        )}
      </>
    );
  };

  return (
    <Controller<T>
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={validationOptionsToControlRules(label, validations)}
      render={({ field }) => (
        <Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Typography variant="titleMedium">{label}:</Typography>
            {labelValueFormatter(field.value)}
          </Box>
          <Box py={1} px={1.5}>
            <Slider
              {...field}
              value={rangeFilterToSliderValue(field.value)}
              defaultValue={
                defaultValue
                  ? rangeFilterToSliderValue(defaultValue)
                  : undefined
              }
              onChange={(_, value) =>
                field.onChange(
                  sliderValueToRangeFilter(value as [number] | [number, number])
                )
              }
              min={adjustedStart}
              max={adjustedEnd}
              step={step}
              valueLabelDisplay="auto"
              valueLabelFormat={(_, index: number) => {
                let value: number;

                if (field.value.type === "any") {
                  return "Any";
                } else if (field.value.type === "between") {
                  if (index === 0) {
                    value = field.value.min;
                  } else {
                    value = field.value.max;
                  }
                } else {
                  value = field.value.value;
                }

                return valueFormatter ? valueFormatter(value) : value;
              }}
              disabled={disabled}
            />
          </Box>
        </Box>
      )}
    />
  );
};

export default APSlider;
